import { useEffect, useState } from 'react';
import { MantineSize, Stack, Text } from '@mantine/core';
import { MatterData, MatterDocument } from '@/database/baseTypes';
import classes from './AltMatterAutocomplete.module.css';
import { WhereClause } from '@/database/queryBuilder';
import PractistSelect, {
  PractistSelectClassNames,
} from '../PractistSelect/PractistSelect';
import { MatterOption } from './types';
import { useMatterSearch } from '@/contexts/MatterSearchContext';

/**
 * A replacement for The MatterAutocomplete component that uses Mantine instead of MUI.
 */
const AltMatterAutocomplete = ({
  inputRef,
  size = 'sm',
  disabled,
  selectClassNames,
  selectedMatterId,
  where,
  onSelect,
  clearable,
  displayLoadingIndicator = true,
  onFocus,
}: {
  inputRef?: React.RefObject<HTMLInputElement>;
  size?: MantineSize;
  selectClassNames?: PractistSelectClassNames;
  selectedMatterId: string | undefined;
  where?: WhereClause<MatterData>[];
  clearable?: boolean;
  onSelect: (
    matter: MatterDocument | undefined,
    selectionMethod: 'keyboard' | 'mouse',
  ) => void;
  displayLoadingIndicator?: boolean;
  disabled?: boolean;
  onFocus?: () => void;
}) => {
  const { loading, search, getMatterById, ensureMatterSearchStarted } =
    useMatterSearch();
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (selectedMatterId) ensureMatterSearchStarted();
  }, [ensureMatterSearchStarted, selectedMatterId]);

  const data = search(searchText, where);

  return (
    <PractistSelect<MatterOption>
      inputRef={inputRef}
      data={data}
      onOptionSelected={(value, selectionMethod) => {
        onSelect(value ? getMatterById(value) : undefined, selectionMethod);
      }}
      placeholder='Search for a matter'
      disabled={disabled}
      loading={loading && displayLoadingIndicator}
      clearable={clearable}
      value={selectedMatterId}
      onSearchChange={setSearchText}
      classNames={selectClassNames}
      onFocus={() => {
        ensureMatterSearchStarted();
        onFocus?.();
      }}
      emptyMessage={loading ? 'Loading matters...' : undefined}
      dropdownWidth='400px'
      size={size}
      renderOption={(option, isActive) => (
        <Stack gap='0.25rem'>
          <Text size={size} fw={500}>
            {option.label}{' '}
            {option.matter.matterType && (
              <Text
                component='span'
                size='xs'
                className={`${classes.secondaryText} ${
                  isActive ? classes.active : ''
                }`}
              >{` · ${option.matter.matterType}`}</Text>
            )}
          </Text>
          <Text
            size='xs'
            className={`${classes.secondaryText} ${
              isActive ? classes.active : ''
            }`}
          >
            {option.matter.scope}
          </Text>{' '}
        </Stack>
      )}
    />
  );
};

export default AltMatterAutocomplete;
